import { createSelector, createFeatureSelector } from '@ngrx/store';

import * as flightUtils from '../../utils/flight.utils';
import * as passengerUtils from '../../utils/passenger.utils';
import { UpgradeState, State } from '../../interfaces/store.interface';
import * as fromData from '../reducers/data.reducer';
import * as fromStatus from '../reducers/status.reducer';
import * as fromBasket from '../reducers/basket.reducer';
import { calculatePaymentTotal } from '../../utils/payment.utils';
import { Balance, PaymentType } from '../../interfaces';
import { VoucherType } from '../../api/models';

export const selectUpgradeState = createFeatureSelector<State, UpgradeState>('upgrade');

// Data Selectors
export const selectDataState = createSelector(selectUpgradeState, (state: UpgradeState) => state.data);

export const getUser = createSelector(selectDataState, fromData.getUser);

export const getBookingRef = createSelector(selectDataState, fromData.getBookingRef);

export const getPassengers = createSelector(selectDataState, fromData.getPassengers);

export const getFlights = createSelector(selectDataState, fromData.getFlights);

export const getMaxPassengers = createSelector(getFlights, flightUtils.flightsMaxPassengers);

export const getJourneys = createSelector(getFlights, flightUtils.flightsToJourneys);

export const getJourneySummary = createSelector(selectDataState, fromData.getJourneySummary);

export const getDeepLinkSource = createSelector(selectDataState, fromData.getDeepLinkSource);

export const getMinimumPaymentOptions = createSelector(selectDataState, fromData.getMinimumPayment);

export const getUpgradeResponse = createSelector(selectDataState, fromData.getUpgradeResponse);

// Status Selectors
export const selectStatusState = createSelector(selectUpgradeState, (state: UpgradeState) => state.status);

export const getStage = createSelector(selectStatusState, fromStatus.getStage);

export const getError = createSelector(selectStatusState, fromStatus.getError);

export const getStatusPending = createSelector(selectStatusState, fromStatus.getPending);

/* use with care. set on success page prior to basket clearance */
export const getWaitlisted = createSelector(selectStatusState, fromStatus.getWaitlisted);

/* use with care. set on success page prior to basket clearance */
export const getSplitPNR = createSelector(selectStatusState, fromStatus.getSplitPNR);

// Basket Selectors
export const selectBasketState = createSelector(selectUpgradeState, (state: UpgradeState) => state.basket);

export const getBasketPassengerIds = createSelector(selectBasketState, fromBasket.getPassengerIds);

export const getBasketFlights = createSelector(getFlights, selectBasketState, (flights, basket) => {
  return basket.flights.map((flightBasket) => {
    return {
      ...flightBasket,
      flight: flights.find((flight) => flight.segmentInfo.flightId === flightBasket.flightId),
    };
  });
});

export const getOriginalBalance = createSelector(getUser, (buyer): Balance => {
  return {
    [PaymentType.EUROPE_UPGRADE]:
      buyer.vouchers?.find((voucher) => voucher.type === VoucherType.EuropeUpgrade)?.count || 0,
    [PaymentType.LONG_HAUL_UPGRADE]:
      buyer.vouchers?.find((voucher) => voucher.type === VoucherType.LongHaulUpgrade)?.count || 0,
    [PaymentType.UPGRADE_BENEFIT]:
      buyer.vouchers?.find((voucher) => voucher.type === VoucherType.UpgradeBenefit)?.count || 0,
    [PaymentType.points]: buyer.points || 0,
  };
});

export const getPaymentTotal = createSelector(
  getBasketFlights,
  getBasketPassengerIds,
  getUser,
  (flightBasket, passengerIds, buyer) => calculatePaymentTotal(flightBasket, passengerIds.length, buyer)
);

export const getMoneyPaymentNotSelected = createSelector(getPaymentTotal, (paymentTotal) => !paymentTotal.money);

export const getBasketPassengerData = createSelector(
  getBasketPassengerIds,
  getPassengers,
  passengerUtils.enrichSelectedPaxWithInfo
);

export const getChildIsTravellingAlone = createSelector(
  getBasketPassengerData,
  getPassengers,
  passengerUtils.childIsTravellingAlone
);

// Modal Selectors
export const selectModalState = createSelector(selectUpgradeState, (state: UpgradeState) => state.modal);
